import { Receipt } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useI18n } from '@/hooks/useI18n';
import { formatPlanAmount } from '@/lib/builder/utils/payment-format';

interface PlatformFeeDueBadgeProps {
    /** null quando le commissioni non riguardano chi guarda (il viaggiatore). */
    amount: number | null | undefined;
}

/**
 * Commissione da versare, accanto a una rata già incassata.
 *
 * Il badge non racconta lo stato della rata — quella è pagata, e il suo badge lo dice —
 * ma una posizione aperta fra l'agenzia e noi. Sono due cose diverse e stanno affiancate
 * proprio per non confonderle: fonderle in un unico stato "pagata ma…" rimetterebbe in
 * dubbio un incasso che invece è avvenuto.
 *
 * A vista solo icona e cifra, per stare nelle righe compatte delle rate; la dicitura
 * completa è nel tooltip.
 */
export const PlatformFeeDueBadge = ({ amount }: PlatformFeeDueBadgeProps) => {
    const { t } = useI18n();

    if (amount == null || amount <= 0) return null;

    const formatted = formatPlanAmount(amount);

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <span
                    tabIndex={0}
                    aria-label={t('payments.platformFee.badgeTooltip', { amount: formatted })}
                    // w-fit: in molti punti il genitore e' un flex-col, che stirerebbe il
                    // badge su tutta la riga. Una larghezza esplicita blocca lo stretch
                    // senza toccare l'allineamento verticale dove il genitore e' una riga.
                    className="inline-flex w-fit flex-none items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 whitespace-nowrap tabular-nums cursor-default"
                >
                    <Receipt className="w-3 h-3 flex-shrink-0" />
                    {formatted}
                </span>
            </TooltipTrigger>
            <TooltipContent>
                {t('payments.platformFee.badgeTooltip', { amount: formatted })}
            </TooltipContent>
        </Tooltip>
    );
};
